/**
 * iso/projection.ts — Isometric Coordinate Transforms
 *
 * The heart of the isometric engine. Every tile, every character, every lamp
 * and bookshelf and potted fern on your windowsill passes through here before
 * it touches a pixel. Keep it fast. Keep it exact.
 *
 * Standard 2:1 isometric projection — the classic choice, the one that made
 * Habbo feel like looking into a doll's house from above. It's not technically
 * "iso" (that's 1:1:1), but it's what we mean when we say home.
 */

// ─── Constants ───────────────────────────────────────────────────────────────

/** Width of one tile diamond in screen pixels */
export const TILE_W = 64;

/** Height of one tile diamond in screen pixels */
export const TILE_H = 32;

/** Screen pixels per unit of world Z (elevation) */
export const Z_SCALE = 16;

/** Half-tile dimensions, pre-computed because we use them everywhere */
export const TILE_W_HALF = TILE_W / 2; // 32
export const TILE_H_HALF = TILE_H / 2; // 16

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface WorldPoint {
  x: number;
  y: number;
}

export interface TilePoint {
  tx: number;
  ty: number;
}

/**
 * Camera state. The world is fixed; the camera slides over it.
 * zoom=1 is native pixel art size. zoom=2 is cozy up-close mode.
 */
export interface Camera {
  /** Screen X of the world origin */
  x: number;
  /** Screen Y of the world origin */
  y: number;
  /** Scale factor — 1 = 100%, 2 = 200% (pixel-doubled) */
  zoom: number;
}

// ─── Core Projection ─────────────────────────────────────────────────────────

/**
 * Project world coordinates to screen pixels.
 *
 * The classic isometric formulas. Note that Z lifts things upward on screen
 * (decreasing Y), which is why we subtract it.
 *
 * @param wx  World X — increases east
 * @param wy  World Y — increases south
 * @param wz  World Z — elevation, increases upward
 * @returns   Screen pixel position before camera is applied
 */
export function worldToScreen(wx: number, wy: number, wz: number = 0, snap = false): ScreenPoint {
  const x = (wx - wy) * TILE_W_HALF;
  const y = (wx + wy) * TILE_H_HALF - wz * Z_SCALE;
  return snap
    ? { x: Math.round(x), y: Math.round(y) }
    : { x, y };
}

/**
 * Reverse-project from screen pixels to world XY coordinates.
 *
 * Used for click detection — where in the world did the player tap?
 * Because isometric projection loses the Z axis (everything at different
 * elevations maps to overlapping screen areas), you must supply the elevation
 * you want to test against.
 *
 * @param sx         Screen X (before camera)
 * @param sy         Screen Y (before camera)
 * @param elevation  World Z to solve for (default 0 = ground)
 * @returns          World X and Y at that elevation
 */
export function screenToWorld(sx: number, sy: number, elevation: number = 0): WorldPoint {
  // Adjust sy to account for elevation lifting the point on screen
  const adjustedSy = sy + elevation * Z_SCALE;

  // Solve the 2×2 system:
  //   sx = (wx - wy) * TILE_W_HALF
  //   adjustedSy = (wx + wy) * TILE_H_HALF
  return {
    x: sx / TILE_W_HALF / 2 + adjustedSy / TILE_H_HALF / 2,
    y: adjustedSy / TILE_H_HALF / 2 - sx / TILE_W_HALF / 2,
  };
}

// ─── Camera Transforms ───────────────────────────────────────────────────────

/**
 * Apply camera transform to a raw world-projected screen point.
 * This maps from "world-space screen coords" to "actual canvas pixels."
 */
export function applyCamera(point: ScreenPoint, camera: Camera): ScreenPoint {
  return {
    x: point.x * camera.zoom + camera.x,
    y: point.y * camera.zoom + camera.y,
  };
}

/**
 * Remove camera transform from canvas pixel coords.
 * This maps from "where the mouse is" back to "world-space screen coords."
 */
export function unapplyCamera(point: ScreenPoint, camera: Camera): ScreenPoint {
  return {
    x: (point.x - camera.x) / camera.zoom,
    y: (point.y - camera.y) / camera.zoom,
  };
}

// ─── Tile Helpers ─────────────────────────────────────────────────────────────

/**
 * Given a canvas pixel position and a camera, return which tile sits under
 * that screen point. This is what drives click-to-navigate and hover effects.
 *
 * We unproject to world coords at Z=0, then floor to get the tile indices.
 *
 * @param sx      Canvas pixel X (from e.g. MouseEvent.offsetX)
 * @param sy      Canvas pixel Y
 * @param camera  Current camera state
 * @returns       Tile grid indices {tx, ty}
 */
export function screenToTile(sx: number, sy: number, camera: Camera): TilePoint {
  const raw = unapplyCamera({ x: sx, y: sy }, camera);
  const world = screenToWorld(raw.x, raw.y, 0);
  return {
    tx: Math.floor(world.x),
    ty: Math.floor(world.y),
  };
}

/**
 * Get the world-space center of a tile at grid position (tx, ty).
 *
 * Tile (0,0) owns world coords (0,0) to (1,1), so its center is (0.5, 0.5).
 * This offset means objects placed "on" a tile appear centered on it.
 */
export function tileToWorld(tx: number, ty: number): WorldPoint {
  return {
    x: tx + 0.5,
    y: ty + 0.5,
  };
}

/**
 * Convenience: go from tile grid position directly to canvas pixels.
 * The most common path for rendering tile-aligned objects.
 *
 * @param tx      Tile X index
 * @param ty      Tile Y index
 * @param wz      Elevation in world units
 * @param camera  Current camera
 * @returns       Canvas pixel position
 */
export function tileToScreen(tx: number, ty: number, wz: number, camera: Camera): ScreenPoint {
  const world = tileToWorld(tx, ty);
  const screen = worldToScreen(world.x, world.y, wz);
  return applyCamera(screen, camera);
}

// ─── Utility ──────────────────────────────────────────────────────────────────

/**
 * Build a default camera that centers the isometric grid of a given size
 * in the canvas. A good starting point for any new room.
 *
 * @param canvasW   Canvas width in pixels
 * @param canvasH   Canvas height in pixels
 * @param gridW     Room width in tiles
 * @param gridH     Room height in tiles
 * @param zoom      Zoom level (default 1)
 */
export function centerCamera(
  canvasW: number,
  canvasH: number,
  gridW: number,
  gridH: number,
  zoom: number = 1
): Camera {
  // The grid spans from tile (0,0) to (gridW, gridH) in world space.
  // Its screen-space bounding box (at zoom=1, origin=(0,0)):
  //   leftmost point:  worldToScreen(0, gridH, 0)
  //   rightmost point: worldToScreen(gridW, 0, 0)
  //   topmost point:   worldToScreen(0, 0, 0)
  //   bottommost point: worldToScreen(gridW, gridH, 0)
  const topLeft = worldToScreen(0, 0, 0);
  const bottomRight = worldToScreen(gridW, gridH, 0);

  const gridScreenW = (bottomRight.x - topLeft.x) * zoom;
  const gridScreenH = (bottomRight.y - topLeft.y) * zoom;

  return {
    x: (canvasW - gridScreenW) / 2 - topLeft.x * zoom,
    y: (canvasH - gridScreenH) / 2 - topLeft.y * zoom,
    zoom,
  };
}
